/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState } from "react";
import { useValidatedAddressInput, useValidatedNonEmptyInput, useValidatedPositiveNumberInput, useValidatedNumberInput, useValidatedCapInput, voidValidatedInput } from "../hooks/validatedInputs";
import BatchItem from "../types/BatchItem";

const ManageVaultContextDefault = {
    addCollateralModalOpen: false,
    setAddCollateralModalOpen: (_open: boolean) => { },
    saveConfirmModalOpen: false,
    setSaveConfirmModalOpen: (_open: boolean) => { },
    nameInput: voidValidatedInput,
    symbolInput: voidValidatedInput,
    governorAdminInput: voidValidatedInput,
    feeReceiverInput: voidValidatedInput,
    interestRateModelInput: voidValidatedInput,
    supplyCapInput: voidValidatedInput,
    borrowCapInput: voidValidatedInput,
    interestFeeInput: voidValidatedInput,
    collaterals: [] as CollateralEntry[],
    clearCollaterals: async () => { },
    addCollateral: (_collateral: CollateralEntry | CollateralEntry[]) => { },
    setCollaterals: (_collaterals: CollateralEntry[]) => { },
    setBorrowLtv: (_vaultAddress: `0x${string}`, _ltv: number) => { },
    setLiquidationLtv: (_vaultAddress: `0x${string}`, _ltv: number) => { },
    setLtvs: (_vaultAddress: `0x${string}`, _liquidationLtv: number, _borrowLtv: number) => { },
    setRampDuration: (_vaultAddress: `0x${string}`, _duration: number) => { },
    batchItems: [] as BatchItem[],
    setBatchItems: (_items: BatchItem[]) => { },
    addBatchItem: async (_item: BatchItem | BatchItem[]) => { },
    clearBatchItems: async () => { },
    perspectivesToVerify: {} as perspectivesToVerify,
    setVerifyPerspective: (_perspective: `0x${string}`, _verify: boolean) => { },
    hookTargetInput: voidValidatedInput,
    hookFlags: [false, false, false, false, false, false, false, false, false, false, false, false, false, false, false, false] as HookFlags,
    setHookFlags: (_flags: HookFlags) => { },
    configFlags: [false, false] as ConfigFlags,
    setConfigFlags: (_flags: ConfigFlags) => { },
    maxLiquidationDiscountInput: voidValidatedInput,
    liquidationCoolOffTimeInput: voidValidatedInput,
    addToRewardStreamModalOpen: false,
    setAddToRewardStreamModalOpen: (_open: boolean) => { },
}

type ManageVaultContextType = typeof ManageVaultContextDefault;

const ManageVaultContext = createContext<ManageVaultContextType>(ManageVaultContextDefault);

interface ManageVaultContextProviderProps {
    children: React.ReactNode
}

// 16 flags
export type HookFlags = [boolean, boolean, boolean, boolean, boolean, boolean, boolean, boolean, boolean, boolean, boolean, boolean, boolean, boolean, boolean, boolean]
// 2 flags 
export type ConfigFlags = [boolean, boolean]

interface perspectivesToVerify {
    [key: `0x${string}`]: boolean;
}


export interface CollateralEntry {
    vaultAddress: `0x${string}`,
    liquidationLtv: number,
    borrowLtv: number,
    rampDuration: number,
    prevLiquidationLtv?: number,
    prevBorrowLtv?: number,
}

const ManageVaultContextProvider = ({ children }: ManageVaultContextProviderProps) => {

    const [collaterals, setCollaterals] = useState<CollateralEntry[]>([]);
    const [batchItems, setBatchItems] = useState<BatchItem[]>([]);
    const [perspectivesToVerify, setPerspectivesToVerify] = useState<perspectivesToVerify>({});
    const [hookFlags, setHookFlags] = useState<HookFlags>(ManageVaultContextDefault.hookFlags);
    const [configFlags, setConfigFlags] = useState<ConfigFlags>(ManageVaultContextDefault.configFlags);

    const clearCollaterals = async () => {
        await setCollaterals([]);
    }

    const addCollateral = (newCollaterals: CollateralEntry | CollateralEntry[]) => {
        const newCollateralsArray = Array.isArray(newCollaterals) ? newCollaterals : [newCollaterals];
        const filteredNewCollaterals = newCollateralsArray.filter(c => !collaterals.some(c2 => c2.vaultAddress === c.vaultAddress));
        setCollaterals(collaterals.concat(filteredNewCollaterals));
    }

    const addBatchItem = async (items: BatchItem | BatchItem[]) => {
        await setBatchItems(batchItems.concat(items));
    }

    const clearBatchItems = async () => {
        await setBatchItems([]);
    }

    const setVerifyPerspective = (perspective: `0x${string}`, verify: boolean) => {
        setPerspectivesToVerify({
            ...perspectivesToVerify,
            [perspective]: verify
        });
    }

    const setLiquidationLtv = (vaultAddress: `0x${string}`, ltv: number) => {
        const newCollaterals = collaterals.map(c => {
            if (c.vaultAddress === vaultAddress) {
                return {
                    ...c,
                    liquidationLtv: ltv
                }
            }
            return c;
        });
        setCollaterals(newCollaterals);
    }

    const setBorrowLtv = (vaultAddress: `0x${string}`, ltv: number) => {
        const newCollaterals = collaterals.map(c => {
            if (c.vaultAddress === vaultAddress) {
                return {
                    ...c,
                    borrowLtv: ltv
                }
            }
            return c;
        });
        setCollaterals(newCollaterals);
    }

    const setLtvs = (vaultAddress: `0x${string}`, liquidationLtv: number, borrowLtv: number) => {
        const newCollaterals = collaterals.map(c => {
            if (c.vaultAddress === vaultAddress) {
                return {
                    ...c,
                    liquidationLtv,
                    borrowLtv
                }
            }
            return c;
        });
        setCollaterals(newCollaterals);
    }

    const setRampDuration = (vaultAddress: `0x${string}`, duration: number) => {
        const newCollaterals = collaterals.map(c => {
            if (c.vaultAddress === vaultAddress) {
                return {
                    ...c,
                    rampDuration: duration
                }
            }
            return c;
        });
        setCollaterals(newCollaterals);
    }

    const [addCollateralModalOpen, setAddCollateralModalOpen] = useState(false);
    const [saveConfirmModalOpen, setSaveConfirmModalOpen] = useState(false);
    const nameInput = useValidatedNonEmptyInput();
    const symbolInput = useValidatedNonEmptyInput();
    const governorAdminInput = useValidatedAddressInput();
    const feeReceiverInput = useValidatedAddressInput();
    const interestRateModelInput = useValidatedAddressInput();
    const supplyCapInput = useValidatedCapInput();
    const borrowCapInput = useValidatedCapInput();
    // Min and max should be enforced by the input component
    const interestFeeInput = useValidatedNumberInput({ min: 0, max: 50 }, "5");
    const hookTargetInput = useValidatedAddressInput();
    const maxLiquidationDiscountInput = useValidatedPositiveNumberInput();
    const liquidationCoolOffTimeInput = useValidatedPositiveNumberInput();
    const [addToRewardStreamModalOpen, setAddToRewardStreamModalOpen] = useState(false);

    return (
        <ManageVaultContext.Provider value={{
            addCollateralModalOpen,
            setAddCollateralModalOpen,
            saveConfirmModalOpen,
            setSaveConfirmModalOpen,
            nameInput,
            symbolInput,
            governorAdminInput,
            feeReceiverInput,
            interestRateModelInput,
            supplyCapInput,
            borrowCapInput,
            interestFeeInput,
            collaterals,
            clearCollaterals,
            addCollateral,
            setCollaterals,
            setBorrowLtv,
            setLiquidationLtv,
            setLtvs,
            setRampDuration,
            batchItems,
            setBatchItems,
            addBatchItem,
            clearBatchItems,
            perspectivesToVerify,
            setVerifyPerspective,
            hookTargetInput,
            hookFlags,
            setHookFlags,
            maxLiquidationDiscountInput,
            configFlags,
            setConfigFlags,
            liquidationCoolOffTimeInput,
            addToRewardStreamModalOpen,
            setAddToRewardStreamModalOpen
        }}>
            {children}
        </ManageVaultContext.Provider>
    )

}

const useManageVaultContext = () => {
    return useContext(ManageVaultContext);
}

export {
    ManageVaultContextProvider,
    ManageVaultContext,
    useManageVaultContext
};